import React, { useState } from 'react';
import { transcriptsService } from '../../services/transcripts';
import { exportsService } from '../../services/exports';
import { Export } from '../../types';

interface ExportOptionsProps {
  audioFileId: string;
}

const ExportOptions: React.FC<ExportOptionsProps> = ({ audioFileId }) => {
  const [isExporting, setIsExporting] = useState<{
    pdf: boolean;
    docx: boolean;
  }>({
    pdf: false,
    docx: false,
  });
  const [exports, setExports] = useState<{ pdf?: Export; docx?: Export }>({});

  const downloadFromUrl = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pollExportStatus = async (
    exportRecord: Export,
    format: 'pdf' | 'docx'
  ) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes with 5-second intervals

    const poll = async (): Promise<void> => {
      if (attempts >= maxAttempts) {
        setIsExporting((prev) => ({ ...prev, [format]: false }));
        console.error(`Export ${format.toUpperCase()} timed out`);
        return;
      }

      attempts++;

      try {
        const response = await exportsService.getExport(exportRecord.id);
        const updatedExport = response.data;

        setExports((prev) => ({ ...prev, [format]: updatedExport }));

        if (updatedExport.status === 'completed') {
          // Export is ready, get download URL
          const downloadResponse = await exportsService.downloadExport(
            updatedExport.id
          );
          downloadFromUrl(
            downloadResponse.download_url,
            downloadResponse.filename
          );
          setIsExporting((prev) => ({ ...prev, [format]: false }));
        } else if (updatedExport.status === 'failed') {
          console.error(`Export ${format.toUpperCase()} failed`);
          setIsExporting((prev) => ({ ...prev, [format]: false }));
        } else {
          // Still processing, poll again
          setTimeout(poll, 5000); // Poll every 5 seconds
        }
      } catch (error) {
        console.error(`Failed to check export status:`, error);
        setIsExporting((prev) => ({ ...prev, [format]: false }));
      }
    };

    poll();
  };

  const handleExportPDF = async () => {
    setIsExporting((prev) => ({ ...prev, pdf: true }));
    try {
      const response = await transcriptsService.requestExport(
        audioFileId,
        'pdf'
      );
      const exportRecord = response.data;
      setExports((prev) => ({ ...prev, pdf: exportRecord }));

      // Start polling for completion
      pollExportStatus(exportRecord, 'pdf');
    } catch (error) {
      console.error('Failed to request PDF export:', error);
      setIsExporting((prev) => ({ ...prev, pdf: false }));
    }
  };

  const handleExportDOCX = async () => {
    setIsExporting((prev) => ({ ...prev, docx: true }));
    try {
      const response = await transcriptsService.requestExport(
        audioFileId,
        'docx'
      );
      const exportRecord = response.data;
      setExports((prev) => ({ ...prev, docx: exportRecord }));

      // Start polling for completion
      pollExportStatus(exportRecord, 'docx');
    } catch (error) {
      console.error('Failed to request DOCX export:', error);
      setIsExporting((prev) => ({ ...prev, docx: false }));
    }
  };

  const getButtonText = (format: 'pdf' | 'docx') => {
    const exportRecord = exports[format];
    const isLoading = isExporting[format];

    if (!isLoading) {
      return format === 'pdf' ? '📄 Export as PDF' : '📝 Export as DOCX';
    }

    if (exportRecord) {
      switch (exportRecord.status) {
        case 'pending':
          return `⏳ Queuing ${format.toUpperCase()}...`;
        case 'processing':
          return `⚙️ Generating ${format.toUpperCase()}...`;
        case 'completed':
          return `✅ Downloading ${format.toUpperCase()}...`;
        case 'failed':
          return `❌ ${format.toUpperCase()} Failed`;
        default:
          return `⏳ Processing ${format.toUpperCase()}...`;
      }
    }

    return `⏳ Requesting ${format.toUpperCase()}...`;
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Export Options
      </h4>
      <div className="space-y-2">
        <button
          onClick={handleExportPDF}
          disabled={isExporting.pdf}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800"
        >
          {getButtonText('pdf')}
        </button>
        <button
          onClick={handleExportDOCX}
          disabled={isExporting.docx}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800"
        >
          {getButtonText('docx')}
        </button>
      </div>
    </div>
  );
};

export default ExportOptions;
